import React from 'react';
import { toast } from 'sonner';

import type {
  Notification,
  NotificationResponse,
} from '../types/notification.types';

export const parseNotificationResponse = (
  responseData: NotificationResponse,
) => {

  // Handle the new guaranteed structure with items and meta
  if (responseData && responseData.items && responseData.meta) {

    const { items, meta } = responseData;

    return {
      notifications: items,
      totalPages: meta.totalPages,
      totalNotifications: meta.total,
      hasMore: meta.hasNextPage,
    };
  }

  // Fallback for unexpected response format
  console.error('Unexpected API response format:', responseData);
  return {
    notifications: [],
    totalPages: 1,
    totalNotifications: 0,
    hasMore: false,
  };
};

export const getToastMethod = (
  type: string,
): 'success' | 'error' | 'info' | 'warning' => {
  const toastType = type.toLowerCase();
  if (['success', 'error', 'info', 'warning'].includes(toastType)) {
    return toastType as 'success' | 'error' | 'info' | 'warning';
  }
  return 'info';
};

export const isPageActive = (): boolean => {
  return document.visibilityState === 'visible';
};

// Markdown renderer for toast notifications
const renderMarkdownForToast = (message: string): React.ReactNode => {
  if (!message) return null;

  // Check if message contains markdown
  const hasMarkdown = /(\*\*|\*|`|- )/.test(message);

  if (!hasMarkdown) {
    return <span className="text-xs leading-relaxed">{message}</span>;
  }

  // Split message into lines for processing
  const lines = message.split('\n');
  const elements: React.ReactNode[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    if (!line) {
      // Add spacing for empty lines
      elements.push(<div key={`space-${i}`} className="h-0.5" />);
      continue;
    }

    // Handle list items
    if (line.startsWith('- ')) {
      const listContent = line.slice(2); // Remove "- " prefix
      const processedContent = processInlineMarkdownForToast(listContent);
      elements.push(
        <div key={i} className="mt-0.5 flex items-start gap-1.5">
          <span className="mt-0.5 text-[10px] font-bold text-blue-500">•</span>
          <div className="text-xs leading-relaxed">{processedContent}</div>
        </div>,
      );
    }
    // Handle regular paragraphs
    else {
      const processedLine = processInlineMarkdownForToast(line);
      elements.push(
        <div key={i} className="mt-0.5 text-xs leading-relaxed">
          {processedLine}
        </div>,
      );
    }
  }

  return <div className="max-w-sm space-y-0.5">{elements}</div>;
};

// Process inline markdown for toast
const processInlineMarkdownForToast = (text: string): React.ReactNode => {
  if (!text) return null;

  const cleanText = text.trim();

  // If no markdown patterns found, return as plain text
  if (!/(\*\*.*?\*\*|\*.*?\*|`.*?`)/.test(cleanText)) {
    return cleanText;
  }

  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  let keyCounter = 0;

  // Regex patterns for markdown
  const patterns = [
    { regex: /\*\*([^*]+?)\*\*/g, component: 'bold' },
    { regex: /\*([^*]+?)\*/g, component: 'italic' },
    { regex: /`([^`]+?)`/g, component: 'code' },
  ];

  // Find all markdown matches
  const matches: {
    start: number;
    end: number;
    content: string;
    type: string;
  }[] = [];

  patterns.forEach(({ regex, component }) => {
    const tempRegex = new RegExp(regex.source, regex.flags);
    let match: RegExpExecArray | null;

    tempRegex.lastIndex = 0;

    while ((match = tempRegex.exec(cleanText)) !== null) {
      const overlaps = matches.some(
        (existingMatch) =>
          (match!.index >= existingMatch.start &&
            match!.index < existingMatch.end) ||
          (match!.index + match![0].length > existingMatch.start &&
            match!.index + match![0].length <= existingMatch.end),
      );

      if (!overlaps) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
          content: match[1],
          type: component,
        });
      }
    }
  });

  // Sort matches by position
  matches.sort((a, b) => a.start - b.start);

  if (matches.length === 0) {
    return cleanText;
  }

  // Process text with markdown
  matches.forEach((match) => {
    // Add text before the match
    if (currentIndex < match.start) {
      const beforeText = cleanText.slice(currentIndex, match.start);
      if (beforeText) {
        parts.push(<span key={`text-${keyCounter++}`}>{beforeText}</span>);
      }
    }

    // Add the styled content
    switch (match.type) {
      case 'bold':
        parts.push(
          <span
            key={`bold-${keyCounter++}`}
            className="font-semibold text-foreground"
          >
            {match.content}
          </span>,
        );
        break;
      case 'italic':
        parts.push(
          <span key={`italic-${keyCounter++}`} className="italic">
            {match.content}
          </span>,
        );
        break;
      case 'code':
        parts.push(
          <code
            key={`code-${keyCounter++}`}
            className="rounded bg-muted px-0.5 py-0.5 font-mono text-[10px]"
          >
            {match.content}
          </code>,
        );
        break;
    }

    currentIndex = match.end;
  });

  // Add remaining text
  if (currentIndex < cleanText.length) {
    const remainingText = cleanText.slice(currentIndex);
    if (remainingText) {
      parts.push(<span key={`text-${keyCounter++}`}>{remainingText}</span>);
    }
  }

  return parts.length > 0 ? <>{parts}</> : cleanText;
};

export const showNotificationToast = (
  notification: Notification,
  onMarkAsRead: (id: string) => void,
): void => {
  const toastMethod = getToastMethod(notification.type);

  toast[toastMethod](notification.title, {
    description: renderMarkdownForToast(notification.message),
    id: notification._id,
    duration: 3000, // 3 seconds
    position: 'bottom-right',
    className: 'notification-toast',
    classNames: {
      title: 'text-xs font-semibold',
      description: 'text-xs',
      toast: 'max-w-sm border shadow-lg text-xs',
    },
    onDismiss: () => onMarkAsRead(notification._id),
    action: {
      label: 'تم القراءة',
      onClick: () => onMarkAsRead(notification._id),
    },
  });
};

export const showBrowserNotification = async (
  notification: Notification,
  onMarkAsRead: (id: string) => void,
): Promise<void> => {
  try {
    // Strip markdown for browser notifications (they don't support HTML)
    const plainTextMessage = notification.message
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/`(.*?)`/g, '$1')
      .replace(/^- /gm, '• ');

    const browserNotification = new window.Notification('HealthyZone', {
      body: `${notification.title}\n${plainTextMessage}`,
      icon: '/logo.jpg',
      badge: '/logo.jpg',
      tag: notification._id, // Prevents duplicate notifications
      requireInteraction: true, // Keeps notification visible until user interacts
    });

    browserNotification.onclick = async () => {
      window.focus();
      onMarkAsRead(notification._id);
      browserNotification.close();
    };

    // Auto close after 10 seconds if user doesn't interact
    setTimeout(() => {
      browserNotification.close();
    }, 10000);
  } catch (error) {
    console.error('Browser notification error', error);
  }
};

export const requestBrowserNotificationPermission =
  async (): Promise<NotificationPermission> => {
    if (!('Notification' in window)) return 'denied';

    try {
      const permission = await window.Notification.requestPermission();
      return permission;
    } catch {
      return 'denied';
    }
  };
